import { FC } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { X, History } from 'lucide-react';
import { type UserProject } from '@teachur/http-client';
import { ProjectStatusBadge } from '../ProjectStatusBadge/ProjectStatusBadge';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { SubmissionDetailContent } from '@new/common/ui/SubmissionDetail/SubmissionDetailContent';
import { toProjectSubmissionDetail } from '@new/common/ui/SubmissionDetail/submissionDetail.mappers';

interface SubmissionsHistorySidePanelProps {
  project: UserProject | null;
  onClose: () => void;
}

export const SubmissionsHistorySidePanel: FC<SubmissionsHistorySidePanelProps> = ({
  project,
  onClose,
}) => {
  const submissions = project?.submissions
    ? [...project.submissions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    : [];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-(--neutral-white-NW800) mr-[2%]">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-(--neutral-black-NB500)" />
          <h3 className="[font-size:var(--fs-h6)]! font-bold text-(--neutral-black-NB1000)! mb-0!">
            Submission History
          </h3>
        </div>
        <button
          onClick={onClose}
          className="rounded-md text-(--neutral-black-NB500) hover:text-(--neutral-black-NB900) cursor-pointer"
          aria-label="Close submission history"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <ScrollArea.Root className="flex-1 min-h-0 overflow-hidden">
        <ScrollArea.Viewport className="w-full h-full p-4 mr-[2%]">
          {submissions.length === 0 ? (
            <EmptyState
              message="No previous submissions"
              description="This is the first submission for this project."
            />
          ) : (
            <div className="flex flex-col gap-4">
              {submissions.map((submission) => {
                const statusBadge = submission.status ? (
                  <ProjectStatusBadge status={submission.status} />
                ) : undefined;

                return (
                  <div
                    key={submission.id}
                    className="rounded-[12px] border border-(--neutral-white-NW800) p-4 bg-(--neutral-white-NW1000)"
                  >
                    <SubmissionDetailContent
                      data={toProjectSubmissionDetail(project, submission, statusBadge)}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea.Viewport>
        <ScrollArea.Scrollbar
          orientation="vertical"
          className="flex w-2.5 bg-transparent p-0.5 hover:bg-gray-100 transition-colors mr-1"
        >
          <ScrollArea.Thumb className="flex-1 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors" />
        </ScrollArea.Scrollbar>
        <ScrollArea.Corner className="bg-gray-100" />
      </ScrollArea.Root>
    </div>
  );
};
